import { useModals } from "@/components/hooks/useModals";
import { useState } from "react";
import PlayerStage, { PlayerStageProps } from "./player-stage";
import { HighlightInterface, ProductItemInterface, StoreInterface } from "@/assets/interfaces";
import { useFetcher } from "@/api/utils";
import { GetStoreHighlights } from "@/api/items";
import Portal from "@/components/portal";
import RoundActionButton from "@/components/ui/buttons/round-action-button";
import useLockPageScroll from "@/components/hooks/useLockPageScroll";
import classNames from "classnames";

interface HighlightPreviewRingsProps {
  highlights: HighlightInterface[];
  onClick: (index: number) => void;
  isLoading: boolean;
  expectedHighlights: number;
}

const HighlightPreviewRings = ({ highlights, onClick, isLoading, expectedHighlights }: HighlightPreviewRingsProps) => {
  if (isLoading) {
    return (
      <div className="carousel-responsive-padding min-w-full overflow-y-auto">
        <div className="flex gap-6 sm:gap-10 overflow-x-auto py-6.25">
          {Array.from({ length: expectedHighlights }).map((_, index) => (
            <li className="flex flex-col items-center" onClick={() => onClick(index)}>
              <div className="border-2 border-grey-divider p-0.5 rounded-full overflow-hidden mb-2">
                <div className="w-17.5 h-17.5 sm:h-20 sm:w-20 bg-grey-loader animate-pulse rounded-full"></div>
              </div>
              <div className="h-3 bg-grey-loader animate-pulse w-20 rounded-15"></div>
            </li>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="carousel-responsive-padding w-full overflow-x-auto">
      <div className="flex gap-6 sm:gap-10 overflow-x-auto py-6.25">
        {highlights.map((highlight, index) => (
          <li className="flex flex-col items-center flex-shrink-0" onClick={() => onClick(index)}>
            <div className="high-gradient p-0.5 rounded-full overflow-hidden">
              <button className="block bg-white p-0.5 rounded-full transform transition hover:scale-105 h-17.5 w-17.5 sm:h-20 sm:w-20 overflow-hidden">
                <img
                  className="w-full h-full rounded-full object-cover object-center"
                  src={highlight.videos[0].thumbnail}
                  alt={highlight.title}
                />
              </button>
            </div>
            <span className="text-1xs sm:text-sm text-black-secondary mt-1 font-medium flex-shrink-0">
              {highlight.title}
            </span>
          </li>
        ))}
      </div>
    </div>
  );
};

interface PlayerStageInModalProps extends PlayerStageProps {}

const PlayerStageInModal = (props: PlayerStageInModalProps) => {
  const { show, toggle } = props;
  useLockPageScroll(show);

  return (
    <Portal>
      <div
        className={classNames(
          "fixed top-0 left-0 h-[100dvh] w-full bg-black z-[990] flex items-center justify-center transition-opacity duration-300 overflow-hidden",
          {
            "opacity-100": show,
            "opacity-0 pointer-events-none": !show,
          }
        )}
      >
        <RoundActionButton
          onClick={() => toggle()}
          className="bg-transparent !text-white absolute top-3 right-3 z-[10]"
        >
          {/* prettier ignore */}
          <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M18.8125 6L6.8125 18M6.8125 6L18.8125 18"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </RoundActionButton>
        <div className="h-full flex items-center justify-center w-full">
          <PlayerStage {...props} />
        </div>
      </div>
    </Portal>
  );
};

interface HighlightProps {
  expectedHighlights: number;
  openItem: (item: ProductItemInterface) => void;
  store: StoreInterface;
}

const Highlights = ({ expectedHighlights, store, openItem }: HighlightProps) => {
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const { modals, toggleModal } = useModals(["player_stage"]);

  const getHighlightsReq = useFetcher(GetStoreHighlights, {
    store: store.id,
  });

  const highlights: HighlightInterface[] = getHighlightsReq?.response?.data?.items ?? [];

  const handleHighlightClick = (index: number) => {
    setSelectedIndex(index);
    toggleModal("player_stage");
  };

  return (
    <>
      <HighlightPreviewRings
        highlights={highlights}
        onClick={handleHighlightClick}
        expectedHighlights={expectedHighlights}
        isLoading={getHighlightsReq.isLoading}
      />
      {selectedIndex !== null && highlights.length > 0 && (
        <PlayerStageInModal
          show={modals.player_stage}
          store={store}
          toggle={() => toggleModal("player_stage")}
          initialHighlightIndex={selectedIndex}
          highlights={highlights}
          openItem={openItem}
        />
      )}
    </>
  );
};

export default Highlights;
